import React, { useEffect, useState } from 'react'
import { Form, InputGroup, Alert } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { getValidPreviousIntentId } from '../../../../utils/functions/getValidPreviousIntentId'
import { updateWhatsAppIntent } from '../../../../utils/functions/updateWhatsAppIntent'
import useWebhookState from '../../../../utils/hooks/useWebhookState'
import WhatsappWebhookComponent from './WhatsappWebhookComponent'
import {
  initializeTags,
  addTagToSession,
  removeTagFromSession,
  isTagAvailable
} from '../../../../utils/functions/tagUtils'

interface IProps {
  intent: any
  inputJson: any[]
  setInputJson: React.Dispatch<React.SetStateAction<any[]>>
  closeModal: () => void
  data: any
}

const TextWithUrl: React.FC<IProps> = ({ intent, inputJson, setInputJson, closeModal, data }) => {
  const intentIndex = inputJson.findIndex((item) => item.id === intent.id)
  const { isWebhookChecked, handleWebhookCheckboxChange, handleFulfillmentChange } =
    useWebhookState(intent, intentIndex, inputJson, setInputJson, null)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    initializeTags()
  }, [])

  const handleBodyChange = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    const value = e.target.value
    const name = e.target.name
    const newInputJson = [...inputJson]
    newInputJson[intentIndex] = {
      ...intent,
      text: {
        ...intent.text,
        body: value
      }
    }
    setInputJson(newInputJson)

    if (name === 'tag') {
      const currentTag = intent?.tag || ''

      if (!isTagAvailable(value, currentTag)) {
        toast.error('This tag name is already in use. Please choose another name.')
        e.target.value = currentTag
        return
      }

      // Remove old tag from session if it exists
      if (currentTag && currentTag.trim() !== '') {
        removeTagFromSession(currentTag)
      }

      // Add new tag to session
      if (value && value.trim() !== '') {
        const tagAdded = addTagToSession(value)
        if (tagAdded) {
          newInputJson[intentIndex] = {
            ...intent,
            tag: value.trim()
          }
          setInputJson(newInputJson)
        }
      } else {
        newInputJson[intentIndex] = {
          ...intent,
          tag: value
        }
        setInputJson(newInputJson)
      }
    }
  }

  const handleAutoTriggerChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    const newInputJson = [...inputJson]
    newInputJson[intentIndex] = {
      ...intent,
      auto_trigger: value === 'true'
    }
    setInputJson(newInputJson)
  }

  const handleAddAutoTrigger = () => {
    if (intent.auto_trigger !== undefined) {
      toast.warning('Auto trigger already exists for this intent')
      return
    }

    const newInputJson = [...inputJson]
    newInputJson[intentIndex] = {
      ...intent,
      auto_trigger: true // default when added
    }
    setInputJson(newInputJson)
  }

  const handleDeleteAutoTrigger = () => {
    const newInputJson = [...inputJson]
    delete newInputJson[intentIndex].auto_trigger
    setInputJson(newInputJson)
  }

  const handleSave = async () => {
    const token = localStorage.getItem('auth-token')
    const whatsAppIDString = data?.whatsAppIDString
    const previousIntentId = getValidPreviousIntentId(
      intentIndex,
      inputJson,
      data?.WhatsAppInput || []
    )
    setIsSaving(true)
    await updateWhatsAppIntent({
      token,
      whatsAppIDString,
      intent,
      intentJson: inputJson[intentIndex],
      previousIntentId,
      onSuccess: closeModal
    })
  }

  return (
    <>
      <h5 className='card-title h4 mb-5'>Text With URL</h5>

      <InputGroup className='my-3'>
        <InputGroup.Text>Tag</InputGroup.Text>
        <Form.Control
          id={`name-${intent.id}`}
          type='text'
          name='tag'
          defaultValue={intent?.tag}
          onBlur={handleBodyChange}
        />
      </InputGroup>

      <InputGroup className='my-3'>
        <InputGroup.Text>Message Body</InputGroup.Text>
        <Form.Control
          as='textarea'
          rows={4}
          name='textBody'
          maxLength={1024}
          defaultValue={intent.text?.body || ''}
          onBlur={handleBodyChange}
        />
      </InputGroup>

      {/* --- Auto Trigger Handling --- */}
      <div className='my-3'>
        {!('auto_trigger' in intent) ? (
          <>
            <button className='btn btn-light btn-primary' onClick={handleAddAutoTrigger}>
              + Add Auto Trigger
            </button>
            <small className='text-muted d-block mt-2'>
              If you click the button, an auto trigger field is added and the next intent will get
              called immediately after this intent.
            </small>
          </>
        ) : (
          <>
            <InputGroup className='mt-3'>
              <InputGroup.Text>Auto Trigger</InputGroup.Text>
              <Form.Select
                id={`auto_trigger-${intent.id}`}
                name='auto_trigger'
                value={String(intent.auto_trigger)}
                onChange={handleAutoTriggerChange}
              >
                <option value='true'>True</option>
                <option value='false'>False</option>
              </Form.Select>
              <button
                className='btn btn-light btn-danger'
                type='button'
                onClick={handleDeleteAutoTrigger}
              >
                Delete
              </button>
            </InputGroup>

            {intent.auto_trigger === false ? (
              <small className='text-danger d-block mt-2'>
                Note: It is false because this is the last intent. If made false it will create a
                never-ending loop in the bot.
              </small>
            ) : (
              <small className='text-muted d-block mt-2'>
                If you click the button, an auto trigger field is added and the next intent will get
                called immediately after this intent.
              </small>
            )}
          </>
        )}
      </div>

      <div className='d-flex'>
        <label className='me-3 fs-5 fw-bolder mt-3'>
          Webhook
          <div className='form-check form-switch mt-3'>
            <input
              className='form-check-input'
              type='checkbox'
              id='flexSwitchCheckDefault'
              role='switch'
              name='webhookComponents'
              checked={isWebhookChecked}
              onChange={handleWebhookCheckboxChange}
            />
          </div>
        </label>
      </div>
      {/* )} */}

      {isWebhookChecked && (
        <WhatsappWebhookComponent
          inputJson={inputJson}
          intent={intent}
          handleFulfillmentChange={handleFulfillmentChange}
        />
      )}

      <Alert variant='warning'>
        <strong>WhatsApp URL Guidelines:</strong>
        <ul className='mb-0'>
          <li>Ensure the message contains a valid URL.</li>
          <li>Preview will only work for links supported by WhatsApp.</li>
          <li>
            Maximum message length is <strong>1024 characters</strong>.
          </li>
        </ul>
      </Alert>

      <div className='d-flex ms-auto mt-auto'>
        <button className='btn btn-secondary me-4' onClick={closeModal}>
          <i className='bi bi-eye-fill' /> Preview
        </button>
        <button className='btn btn-primary' onClick={handleSave} disabled={isSaving}>
          <i className='bi bi-save' /> {isSaving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </>
  )
}

export default TextWithUrl
